import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NavHeaderComponent } from 'libs/abc/queries/opath/core/src/lib/navHeader/navHeader.component';
import { HeaderButton } from 'libs/abc/queries/opath/core/src/lib/viewModels/layout.viewModel';
import { ButtonSettings, MatchMode, RegExpMode, ReplaceMode, SearchParameter } from '../../models/search.model';
import { TranslationConfiguration } from '../../models/view.model';
import { SharedService } from '../../services/shared.service';
@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    standalone: false
})
export class SearchComponent {
  @ViewChild('header', { static: false }) header: NavHeaderComponent;
  @Input() isHighlightCleared: boolean = false;
  @Input() matcheIndex: number = null;
  @Input() mouseTextSelected: string = '';
  private _searchOccurrence: number = 0;
  @Input() isAllMatchActive: boolean = true;
  @Input()
  set searchOccurence(value: number) {
    this._searchOccurrence = value;
  }
  get searchOccurence(): number {
    return this._searchOccurrence;
  }
  @Output() onSearchParametre: EventEmitter<SearchParameter> = new EventEmitter();
  @Output() onMatchText: EventEmitter<string> = new EventEmitter();
  @Output() onReplaceText: EventEmitter<{ mode: string; replaceText: string }> = new EventEmitter();
  public replaceModeButtons: ButtonSettings[];
  public searchButtons: HeaderButton[] = [];

  public replaceText: string = '';
  public previousSearchText: string = '';
  public currentSearchText: string = '';
  private activeNavigationButton: string = '';
  private activeRegularExpressionButtonList: string[] = [];
  public collapsed: boolean = true;
  public searchResult: string;
  public translation: TranslationConfiguration;

  private searchFocused: boolean = false;

  constructor(private sharedService: SharedService) {
    this.translation = sharedService.translation;
  }

  ngOnInit(): void {
    this.matcheIndex = 0;
    this.refreshButtonsState();
    this.replaceModeButtons = [
      {
        icon: 'cellproperties',
        caption: ReplaceMode.replace_one,
        Tooltip: this.translation.replace.one,
        activeStatus: false,
      },
      {
        icon: 'tableproperties',
        caption: ReplaceMode.replace_all,
        Tooltip: this.translation.replace.all,
        activeStatus: false,
      },
    ];

    this.searchResult = this.translation.search.noResults;
  }

  public refreshButtonsState(): void {
    this.searchButtons = [
      {
        icon: 'growfont',
        caption: RegExpMode.match_case,
        toolTipContent: this.translation.search.matchCase,
        statfullButton: true,
        active: false,
        action: async () => this.handleRegularExpressions(RegExpMode.match_case),
      },
      {
        icon: 'doesnotcontain',
        caption: RegExpMode.whole_word,
        toolTipContent: this.translation.search.matchCase,
        statfullButton: true,
        active: false,
        action: async () => this.handleRegularExpressions(RegExpMode.whole_word),
      },
      {
        icon: 'chevronup',
        caption: MatchMode.search_up,
        toolTipContent: this.translation.search.previousMatch,
        statfullButton: false,
        active: false,
        action: async () => this.handleActiveNavigationButton(MatchMode.search_up),
      },
      {
        icon: 'chevrondown',
        caption: MatchMode.search_down,
        toolTipContent: this.translation.search.matchCase,
        statfullButton: false,
        active: false,
        action: async () => this.handleActiveNavigationButton(MatchMode.search_down),
      },
      {
        icon: 'alignjustify',
        caption: MatchMode.search_all,
        toolTipContent: this.translation.search.matchCase,
        statfullButton: true,
        active: true,
        action: async () => this.handleActiveNavigationButton(MatchMode.search_all),
      },
    ];
  }

  public applyFilter(searchText: string): void {
    this.currentSearchText = searchText;
    this.activeNavigationButton = '';
    this.onSearchParametre.emit({
      currentSearchText: searchText,
      activeRegularExpressionButtonList: this.activeRegularExpressionButtonList,
      initializSearch: null,
      isAllMatchActive: this.isAllMatchActive,
    });
  }

  public handleActiveNavigationButton(caption: MatchMode): void {
    const buttonIndex = this.buttonIndex(caption);
    const button = this.searchButtons[buttonIndex];
    if (button.statfullButton) {
      this.changeButtonStatus(button);
    }

    this.activeNavigationButton = caption;

    if (caption == MatchMode.search_all) {
      this.isAllMatchActive = button.active;
      if (this.searchOccurence > 0) {
        this.onSearchParametre.emit({
          currentSearchText: this.currentSearchText,
          activeRegularExpressionButtonList: this.activeRegularExpressionButtonList,
          initializSearch: null,
          isAllMatchActive: this.isAllMatchActive,
        });
      }
    } else {
      this.disableAllMatchButton();

      if (this.searchOccurence > 0) {
        if (this.isHighlightCleared) {
          this.applyFilter(this.currentSearchText);
        }

        this.onMatchText.emit(caption);
      }
    }
  }

  public handleReplaceText(text: string): void {
    this.replaceText = text;
    if (this.isHighlightCleared && this.currentSearchText.length > 0) {
      this.applyFilter(this.currentSearchText);
    }
  }
  public handleActivereplaceModeButton(replaceModeBtn: ButtonSettings): void {
    if (this.searchOccurence > 0 && this.replaceText.length > 0) {
      if (!this.isHighlightCleared) {
        this.onReplaceText.emit({ mode: replaceModeBtn.caption, replaceText: this.replaceText });
      } else {
        if (replaceModeBtn.caption === ReplaceMode.replace_all) {
          this.applyFilter(this.currentSearchText);
          setTimeout(
            () => this.onReplaceText.emit({ mode: replaceModeBtn.caption, replaceText: this.replaceText }),
            100
          );
        } else {
          this.applyFilter(this.currentSearchText);
          this.onMatchText.emit(MatchMode.search_up);
          setTimeout(
            () => this.onReplaceText.emit({ mode: replaceModeBtn.caption, replaceText: this.replaceText }),
            100
          );
        }
      }
    }
  }

  public searchResultDisplayed(): string {
    if (this.currentSearchText && this.searchOccurence > 0) {
      if (this.isHighlightCleared) {
        return `${this.translation.search.results}: ${this.searchOccurence}`;
      } else if (
        this.activeNavigationButton === MatchMode.search_up ||
        this.activeNavigationButton === MatchMode.search_down ||
        !this.isAllMatchActive
      ) {
        return `${this.matcheIndex + 1} ${this.translation.of} ${this.searchOccurence}`;
      } else {
        return `${this.translation.search.results} : ${this.searchOccurence}`;
      }
    } else {
      return this.translation.search.results;
    }
  }

  private buttonIndex(caption: RegExpMode | MatchMode): number {
    return this.searchButtons.findIndex((button) => button.caption === caption);
  }
  private changeButtonStatus(button: HeaderButton): void {
    button.active = !button.active;
  }
  public handleRegularExpressions(caption: RegExpMode): void {
    const buttonIndex = this.buttonIndex(caption);
    const button = this.searchButtons[buttonIndex];
    if (button.statfullButton) {
      this.changeButtonStatus(button);
    }
    if (button.active) {
      this.activeRegularExpressionButtonList.push(caption);
    } else {
      this.activeRegularExpressionButtonList = this.activeRegularExpressionButtonList.filter(
        (item) => item !== caption
      );
    }
    this.applyFilter(this.currentSearchText);
  }

  public toggleReplaceContainer(): void {
    this.collapsed = !this.collapsed;
  }

  public disableAllMatchButton(): void {
    this.searchButtons.find((button: HeaderButton) => button.caption === MatchMode.search_all).active = false;
    this.isAllMatchActive = false;
  }

  ngAfterViewInit(): void {
    if (!this.searchFocused) {
      this.header.searchInput.instance.focus();
      this.searchFocused = true;
    }
    if (this.mouseTextSelected !== null) {
      this.header.searchInput.value = this.mouseTextSelected;
    }
  }

  ngOnDestroy(): void {
    this.onSearchParametre.emit({
      currentSearchText: '',
      activeRegularExpressionButtonList: [],
    });
  }
}
